import { FilterSelections } from '../store/states/filterSelection';
import {
  FilterConfigDto,
  FilterData,
  MultiSelectFilterData,
} from '../store/states/filterTypes';

function extractOptionValues(
  filterData: MultiSelectFilterData
): FilterSelections[string] | undefined {
  const values =
    filterData.type === 'boolean'
      ? filterData.data.map((o) => o.value)
      : filterData.type === 'date'
        ? filterData.data
        : filterData.data.map((o) => o.value);
  return values.length > 0 ? values : undefined;
}

/**
 * Fills in visible option values for multiselect global filters that have no
 * explicit user selection ("All"). Only activates when at least one global
 * filter already has a selection — otherwise cascading has not narrowed any
 * options and "All" truly means all data. Injection applies only to filters
 * that follow at least one selected filter to their left in the config order.
 */
export function applyEffectiveGlobalSelections(
  rawSelections: FilterSelections,
  validatedData: Record<string, FilterData> | undefined,
  configs: FilterConfigDto[] | undefined
): FilterSelections {
  if (configs == null || configs.length === 0 || validatedData == null) {
    return rawSelections;
  }

  const hasAnySelection = configs.some((c) => c.key in rawSelections);
  if (!hasAnySelection) {
    return rawSelections;
  }

  let effective: FilterSelections | undefined;
  // Whether at least one filter to the left already has a selection.
  let hasLeftSelection = false;

  for (const config of configs) {
    // Must be first as also other than multi-select filters can have a selection
    if (config.key in rawSelections) {
      hasLeftSelection = true;
      continue;
    }

    if (config.display !== 'multi-select') {
      continue;
    }

    if (!hasLeftSelection) {
      continue;
    }

    // Config already confirmed multi-select; response data follows the same shape.
    const values = extractOptionValues(
      validatedData[config.key] as MultiSelectFilterData
    );
    if (values == null) {
      continue;
    }

    effective ??= { ...rawSelections };
    effective[config.key] = values;
  }

  return effective ?? rawSelections;
}
